import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';

/**
 * Estado nunca é só cor: cada tom traz um ícone e um rótulo textual, para quem
 * não distingue as cores e para quem lê por leitor de tela.
 *
 * A borda é inteira, nunca lateral — borda lateral colorida como destaque está
 * na lista do que não fazemos.
 */
import type { Tone as AnyTone } from '@evogard/domain';

/** Aviso neutro não é aviso: `Alert` usa todos os tons menos `neutro`. */
/*
 * Dois tons ficam de fora, e por motivos diferentes.
 *
 * `neutro` porque aviso neutro não é aviso. `progresso` porque ele é situação
 * de SELO — "em andamento" —, e um aviso em tom de progresso pediria ícone e
 * voz de alerta para dizer que está tudo correndo bem. Ver `tone.ts`.
 */
type Tone = Exclude<AnyTone, 'neutro' | 'progresso'>;

/*
 * As chaves são os VALORES de `Tone`, não nomes nossos — o mapa é indexado
 * pela união literal, e traduzi-las quebra a indexação.
 */
// @valor
const tones: Record<Tone, { classes: string; icon: IconName; label: string }> = {
  erro: {
    classes: 'bg-erro-suave border-erro text-erro',
    icon: 'atencao',
    label: 'Erro',
  },
  alerta: {
    classes: 'bg-alerta-suave border-alerta text-alerta',
    icon: 'atencao',
    label: 'Atenção',
  },
  sucesso: {
    classes: 'bg-sucesso-suave border-sucesso text-sucesso',
    icon: 'confirmado',
    label: 'Feito',
  },
  informacao: {
    classes: 'bg-primaria-suave border-primaria-texto text-primaria-texto',
    icon: 'informacao',
    label: 'Informação',
  },
};

export function Alert({
  tone = 'informacao',
  title,
  children,
  className,
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  className?: string;
}) {
  const t = tones[tone];
  return (
    <div
      role={tone === 'erro' ? 'alert' : 'status'}
      className={cn(
        'flex gap-2.5 rounded-borda border px-3 py-2.5 text-sm',
        t.classes,
        className,
      )}
    >
      {/* O ícone herda a cor do tom; o texto abaixo volta para cor de leitura. */}
      <Icon nome={t.icon} className="mt-px" />
      <div className="min-w-0 text-texto">
        <span className="sr-only">{t.label}: </span>
        {title && <p className="font-semibold">{title}</p>}
        {children}
      </div>
    </div>
  );
}
